import { api } from '../api/client';

import { API_URL } from '../utils';
import { signInDevice } from './deviceSessions';

/**
 * Two-step sign-in (TOTP) and the attestation PIN.
 *
 * Two different questions with two different answers: MFA asks whether this
 * ACCOUNT is really them (at sign-in), attestation asks who performed one
 * ACT (at save time, on a device several people share). A shared-device home
 * needs both.
 */

const ME = `${API_URL}/users/me`;

export async function startMfaSetup() {
	const { data } = await api.post(`${ME}/mfa/setup`);
	return data;
}

/** Returns the ten recovery codes — shown ONCE and never retrievable again. */
export async function enableMfa(code) {
	const { data } = await api.post(`${ME}/mfa/enable`, { code });
	return data;
}

export async function disableMfa({ password, code, recoveryCode }) {
	const { data } = await api.post(`${ME}/mfa/disable`, {
		password,
		...(code ? { code } : {}),
		...(recoveryCode ? { recoveryCode } : {}),
	});
	return data;
}

export async function regenerateRecoveryCodes(code) {
	const { data } = await api.post(`${ME}/mfa/recovery-codes`, { code });
	return data;
}

/**
 * The second leg of a login: a challenge plus a code becomes the session. In
 * the phone app it sends its own `device` (the API reads only this leg's), and
 * the answer carries the device session's refresh credential (KPR-1674).
 */
export async function completeMfaLogin({ challenge, code, recoveryCode }) {
	const { data } = await api.post(`${API_URL}/login/mfa`, {
		challenge,
		...signInDevice(),
		...(code ? { code } : {}),
		...(recoveryCode ? { recoveryCode } : {}),
	});
	return data;
}

export async function setAttestationPin({ pin, password }) {
	const { data } = await api.post(`${ME}/pin`, { pin, password });
	return data;
}

export async function clearAttestationPin() {
	const { data } = await api.delete(`${ME}/pin`);
	return data;
}

/**
 * A short-lived signature for one card in one collection — or, with
 * `cardIds` (two or more), ONE signature for the several signing cards one act
 * writes (a multi add submit's 428 lists them as `card_ids`, KPR-1725), so the
 * person types their PIN once. `cardIds` needs the `collectionId`.
 */
export async function attest({ pin, password, collectionId, cardId, cardIds }) {
	const several = Array.isArray(cardIds) && cardIds.length > 1 ? cardIds : null;
	const one = several ? null : (cardId || (Array.isArray(cardIds) ? cardIds[0] : null));
	const { data } = await api.post(`${API_URL}/auth/attest`, {
		...(pin ? { pin } : {}),
		...(password ? { password } : {}),
		...(collectionId ? { collection_id: collectionId } : {}),
		...(several ? { card_ids: several } : {}),
		...(one ? { card_id: one } : {}),
	});
	return data;
}

/** The API asks for a signature with 428 + this code. */
export const needsAttestation = error => error?.response?.data?.code === 'attestation_required';
